import * as ConversationActions from './conversation-actions';
import { IUpdateGroupActivityActionPending } from "./conversation-actions";
import {
    IMessageReceivedAction,
    ISaveMessageFulfilledAction
} from './message-actions';
import { ChatReducerHandler } from "./types";
import { distinctObjectArray } from "src/utils/distinctObjectArray";

export const queryConversationsPending: ChatReducerHandler<ConversationActions.IQueryConversationsPendingAction> =
    (state, action) => {
        const newState = { ...state };
        
        newState.conversations = {
            ...newState.conversations,
            isLoading: true,
            hasMore: false,
        }

        if (action.meta.searchText && !action.meta.isLoadMore) {
            newState.conversations = {
                ...newState.conversations,
                items: state.conversations.items.map(x => {
                    return {
                        ...x,
                        isHidden: true
                    }
                }),
            };
        }

        if (!action.meta.searchText && !action.meta.isLoadMore && !action.meta.isComplementary) {
            newState.conversations = {
                ...newState.conversations,
                items: [],
            };
        }

        return newState;
    };

export const queryConversationsRejected: ChatReducerHandler<ConversationActions.IQueryConversationsRejectedAction> =
    (state) => ({
        ...state,
        conversations: {
            ...state.conversations,
            isLoading: false,
        },
    });

export const queryConversationsFulfilled: ChatReducerHandler<ConversationActions.IQueryConversationsFulfilledAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            isLoading: false,
            hasFetched: true,

            conversationsSkip: action.payload.data.conversationsSkip,
            groupsSkip: action.payload.data.groupsSkip,
            usersSkip: action.payload.data.usersSkip,

            hasMore: action.payload.data.hasMore,

            items: state.conversations.items
                .map((stored) => {
                    let fetched;
                    if (stored.id !== 0) {
                        fetched = action.payload.data.items.find((f) => f.id === stored.id && f.id !== 0);
                    } else if (stored.group && stored.group.name) {
                        fetched = action.payload.data.items.find(
                            (f) => f.id === 0 && f.group && f.group.name === stored.group.name
                        );
                    }

                    if (!fetched) {
                        return stored;
                    }

                    return {
                        ...stored,
                        ...fetched,
                        isHidden: false,
                        lastRead: {
                            ...stored.lastRead,
                            ...fetched.lastRead,
                        },
                        participants: distinctObjectArray([...stored.participants, ...fetched.participants], "id"),
                    };
                })
                .concat(
                    action.payload.data.items.filter((fetched) => {
                        if (fetched.id !== 0) {
                            return !state.conversations.items.some((stored) => stored.id === fetched.id && stored.id !== 0);
                        } else if (fetched.group && fetched.group.name) {
                            return !state.conversations.items.some(
                                (stored) => stored.id === 0 && stored.group && stored.group.name === fetched.group.name
                            );
                        }
                        return true;
                    })
                ),
        },
    });

export const getConversationFulfilled: ChatReducerHandler<ConversationActions.IGetConversationFulfilledAction> =
    (state, action) => {
        const alreadyFetched = state.conversations.items.some(
            (c) => c.id === action.payload.data.id
        );

        if (alreadyFetched) {
            return {
                ...state,
                conversations: {
                    ...state.conversations,
                    items: state.conversations.items.map(
                        (c) => c.id === action.payload.data.id
                            ? {
                                ...c,
                                ...action.payload.data,
                                participants: distinctObjectArray([...action.payload.data.participants, ...c.participants], "id"),
                                isPinned: c.isPinned
                            }
                            : c,
                    ),
                },
            };
        }

        return {
            ...state,
            conversations: {
                ...state.conversations,
                items: state.conversations.items.concat([
                    action.payload.data
                ])
            },
        }
    };

export const setTabState: ChatReducerHandler<ConversationActions.ISetTabStateAction> =
    (state, action) => {
        const tabIndex = state.conversations.tabIds.findIndex(
            (tab) => action.id === tab.id
        );

        if (tabIndex === -1 && action.open === false) {
            return state;
        }

        if (tabIndex > -1 && action.open !== false) {
            const existing = state.conversations.tabIds[tabIndex];

            if (action.open === true) {
                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        tabIds: [
                            ...state.conversations.tabIds.slice(0, tabIndex),
                            ...state.conversations.tabIds.slice(tabIndex + 1),
                            { ...existing, minimized: action.minimized },
                        ],
                    }
                };
            }

            return {
                ...state,
                conversations: {
                    ...state.conversations,
                    tabIds: state.conversations.tabIds.map((tab, index) => index === tabIndex
                        ? { ...tab, minimized: action.minimized }
                        : tab
                    ),
                }
            };
        }

        if (action.open !== false) {
            return {
                ...state,
                conversations: {
                    ...state.conversations,
                    tabIds: state.conversations.tabIds.concat([{
                        id: action.id,
                        minimized: action.minimized,
                    }]),
                },
            }
        }

        return {
            ...state,
            conversations: {
                ...state.conversations,
                tabIds: state.conversations.tabIds.filter(
                    (_, index) => index !== tabIndex
                ),
            },
        };
    };

export const saveMessageFulfilled: ChatReducerHandler<ISaveMessageFulfilledAction> =
    (state, action) => {
        const smfItemIndex = state.conversations.items.findIndex(
            (c) => c.id === action.meta.message.conversationId
        );

        if (smfItemIndex === -1) {
            return state;
        }

        return {
            ...state,
            conversations: {
                ...state.conversations,
                items: state.conversations.items.map((c, index) => index !== smfItemIndex
                    ? c
                    : {
                        ...c,
                        lastMessage: action.payload.data,
                        unread: action.meta.ownId === action.meta.message.user.id
                            ? c.unread
                            : (c.unread || 0) + 1,
                    },
                ),
            },
        };
    };

export const updateGroupActivity: ChatReducerHandler<IUpdateGroupActivityActionPending> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map(
                (c) => c.group?.id !== action.meta
                    ? c
                    : {
                        ...c,
                        group: {
                            ...c.group,
                            unread: 0,
                        },
                    },
            ),
        },
    });

export const addMessageFulfilled: ChatReducerHandler<IMessageReceivedAction> =
    (state, action) => {
        const amfItemIndex = state.conversations.items.findIndex(
            (c) => c.id === action.meta.message.conversationId
        );

        if (amfItemIndex === -1) {
            return state;
        }

        return {
            ...state,
            conversations: {
                ...state.conversations,
                items: state.conversations.items.map((c, index) => index !== amfItemIndex
                    ? c
                    : {
                        ...state.conversations.items[amfItemIndex],
                        lastMessage: action.meta.message,
                        unread: action.meta.ownId === action.meta.message.user.id
                            ? state.conversations.items[amfItemIndex].unread
                            : (state.conversations.items[amfItemIndex].unread || 0) + 1,
                    },
                ),
            },  
        };
    };

export const createConversationPeding: ChatReducerHandler<ConversationActions.ICreateConversationPendingAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.concat([
                action.meta
            ]),
        },
    });

export const createConversationRejected: ChatReducerHandler<ConversationActions.ICreateConversationRejectedAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.filter(
                (c) => c.id !== action.meta.id,
            ),
        },
    });
export const createConversationFulfilled: ChatReducerHandler<ConversationActions.ICreateConversationFulfilledAction> =
    (state, action) => {
        const createdConvAlreadyAdded = state.conversations.items.some(
            (c) => c.id === action.payload.data.id,
        );

        const tempConvIndex = state.conversations.items.findIndex(
            (c) => c.id === action.meta.id,
        );

        if (tempConvIndex === -1) {
            if (createdConvAlreadyAdded) {
                return state;
            }

            return {
                ...state,
                conversations: {
                    ...state.conversations,
                    items: state.conversations.items.concat([action.payload.data])
                },
            };
        }

        const tempTabIdIndex = state.conversations.tabIds.findIndex(
            (t) => t.id === action.meta.id,
        );

        return {
            ...state,
            conversations: {
                ...state.conversations,
                items: state.conversations.items.map(
                    (c, index) => index !== tempConvIndex
                        ? c
                        : action.payload.data
                ),
                tabIds: tempTabIdIndex === -1
                    ? state.conversations.tabIds
                    : state.conversations.tabIds.map(
                        (tab) => tab.id !== action.meta.id
                            ? tab
                            : ({
                                ...tab,

                                id: action.payload.data.id,
                            }),
                        ),
            },
            };
    };

export const addRemoteConversation: ChatReducerHandler<ConversationActions.IAddRemoteConversationAction> =
    (state, action) => state.conversations.items.some((c) => c.id === action.conversation.id)
        ? state
        : {
        ...state,
            conversations: {
                ...state.conversations,
                items: state.conversations.items.concat([action.conversation]),
            },
        };

export const markAsReadPending: ChatReducerHandler<ConversationActions.IMarkAsReadPendingAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map((c) => {
                if (c.id !== action.meta.conversationId) {
                    return c;
                }

                return {
                    ...c,
                    unread: 0
                }
            }),
        }
    });

export const markAsReadRejected: ChatReducerHandler<ConversationActions.IMarkAsReadRejectedAction> =
    (state, action) => !action.meta.prevUnread ? state : {
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map((c) => {
                if (c.id !== action.meta.conversationId) {
                    return c;
                }

                return {
                    ...c,
                    unread: c.unread + action.meta.prevUnread,
                }
            })
        },
    };

export const lastReadReceived: ChatReducerHandler<ConversationActions.ILastReadReceivedAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map(
                (c) => {
                    if (c.id !== action.conversationId) {
                        return c;
                    }

                    const lastRead: { [userId: number]: number } = {
                        ...c.lastRead,

                        [action.userId]: action.messageId,
                    };

                    const c2 = {
                        ...c,
                        lastRead
                    }

                    return c2;
                }
            ),
        },
    });

export const addIsTyping: ChatReducerHandler<ConversationActions.IAddIsTypingAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            isTyping: {
                ...state.conversations.isTyping,
                [action.conversationId]: {
                    ...(state.conversations.isTyping[action.conversationId] || {}),
                    [action.userId]: new Date()
                },
            },
        },
    });

export const removeIsTyping: ChatReducerHandler<ConversationActions.IRemoveIsTypingAction> =
    (state, action) => !state.conversations.isTyping[action.conversationId] 
        ? state 
        : ({
            ...state,
            conversations: {
                ...state.conversations,
                isTyping: {
                    ...state.conversations.isTyping,
                    [action.conversationId]: {
                        ...state.conversations.isTyping[action.conversationId],
                        [action.userId]: null,
                    },
                },
            },
        });

export const setUnreadCountPersonConversations: ChatReducerHandler<ConversationActions.ISetUnreadCountPersonConversations> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            unreadCountPersonConversations: action.meta,
        },
    });

export const setUnreadCountGroupConversations: ChatReducerHandler<ConversationActions.ISetUnreadCountGroupConversations> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            unreadCountGroupConversations: action.meta,
        },
    });

export const updateConversation: ChatReducerHandler<ConversationActions.IUpdateConversation> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map((c) => {
                if (c.id === action.conversation.id) {
                    return action.conversation;
                }

                return c;
            }),
        },
    });

export const setComposerReplyMessage: ChatReducerHandler<ConversationActions.ISetComposerReplyMessage> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            messageToReplyTo: {
                ...state.conversations.messageToReplyTo,
                [action.conversationId]: action.message,
            },
            messageToEdit: {
                ...state.conversations.messageToEdit,
                [action.conversationId]: undefined,
            },
        },
    });

export const setComposerEditMessage: ChatReducerHandler<ConversationActions.ISetComposerEditMessage> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            messageToEdit: {
                ...state.conversations.messageToEdit,
                [action.conversationId]: action.message,
            },
            messageToReplyTo: {
                ...state.conversations.messageToReplyTo,
                [action.conversationId]: undefined,
            },
        },
    });
